/**
 * 1.8.1 fixes (docs/dev/fixes-1.8.1-build.md): the names the three streams share. Additive only.
 */

/** The deepest row the Library pages to (GET /api/v1/assets offset); beyond it 400 OFFSET_TOO_DEEP. */
export const MAX_LIBRARY_OFFSET = 50_000;

/**
 * The 1.8.1 error codes and their HTTP statuses. Errors as `{error: {code, message, details?}}` (HttpError). Messages
 * here are the defaults; FILE_TOO_LARGE_TO_SCAN's `{limit}` is filled in with scanLimitMessage.
 */
export const RELEASE_181_ERRORS = {
  // accounts (stream A): PUT /setup/account once setup is finished.
  SETUP_DONE: { status: 409, message: 'Setup is finished. Change your email and password in My account.' },
  // accounts (stream A): disable or two-step reset of a person who also belongs to a workspace the caller does not administer.
  OTHER_WORKSPACES: { status: 409, message: 'This person also belongs to a workspace you do not administer. Remove them from this workspace instead.' },
  // accounts (stream A): SSO sign-in of a person an administrator removed from the workspace.
  MEMBER_REMOVED: { status: 403, message: 'An administrator removed you from this workspace. Ask them to add you again.' },
  // the Library (stream C): an offset beyond MAX_LIBRARY_OFFSET.
  OFFSET_TOO_DEEP: { status: 400, message: 'Showing the first 50,000. Narrow your search to see the rest.' },
  // the portal (stream C): a non-media file larger than clamd's StreamMaxLength (PRISM_PORTAL_SCAN_MAX_BYTES).
  FILE_TOO_LARGE_TO_SCAN: { status: 422, message: 'This file is too large for the virus scanner. Files other than video and audio can be at most {limit}.' },
} as const satisfies Record<string, { status: number; message: string }>;

export type Release181ErrorCode = keyof typeof RELEASE_181_ERRORS;

/** Bytes as a short size for messages: 104857600 → "100 MB", 1610612736 → "1.5 GB" (binary units, as clamd counts). */
export function formatScanLimit(bytes: number): string {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  let n = Math.max(0, bytes);
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  const text = i === 0 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
  return `${text} ${units[i]}`;
}

/** FILE_TOO_LARGE_TO_SCAN's message with the limit filled in. */
export function scanLimitMessage(limitBytes: number): string {
  return RELEASE_181_ERRORS.FILE_TOO_LARGE_TO_SCAN.message.replace('{limit}', formatScanLimit(limitBytes));
}
